import React, { useEffect, useState } from "react";
import StudentForm, {
    CustomField,
    StudentFormSchema,
} from "../../Forms/StudentForm";
import { ADD_STUDENT, PEOPLE_PAGE_QUERY } from "ft-shared-graphql";
import { useMutation, useQuery, gql, ApolloError, useLazyQuery } from "@apollo/client";
import {
    ApiValidationError,
    FormSubmitCallback,
    mapApiFieldValidationErrors,
} from "ui/Form";
import { useSlideInContext } from "ui/SlideIn";
import {
    AddStudentMutation,
    AddStudentMutationVariables,
    CustomFieldInput,
    LocationsQuery,
    LocationsQueryVariables

} from "ft-shared-graphql";
import { useManageModal } from "ui/Modal";
import { GET_STUDENTS_PAGE, GET_LOCATIONS } from "ft-shared-graphql";
import { LocationDataType } from "ft-shared-graphql/types";
export type AddStudentPayload = {
    name: string,
    streetLineOne?: string,
    streetLineTwo?: string,
    city?: string,
    state?: string,
    postalCode?: string
}
export type AddStudentProps = {
    defaultLocation: LocationDataType,
    parentEmailNeeded: boolean,
    roles: string[],
    onSubmit: () => void
}

export default function AddStudent({ defaultLocation, parentEmailNeeded, roles, onSubmit }: AddStudentProps) {
    const [addStudent, { error }] = useMutation<AddStudentMutation, AddStudentMutationVariables>(ADD_STUDENT, {
        refetchQueries: [GET_STUDENTS_PAGE],
    });
    const { loading: loadingPeople, data: peopleData } = useQuery(PEOPLE_PAGE_QUERY, {
        variables: { role: "PAIR_STUDENT_STAFF_ACCESS", first: 100 },
        fetchPolicy: "cache-first"
    })

    const [customFields, setCustomFields] = useState<CustomField[]>([]);
    const [locationPeople, setLocationPeople] = useState<{ id: string, label: string }[]>([]);
    const { toggleDrawer } = useSlideInContext();
    const { loading, data } = useQuery<LocationsQuery, LocationsQueryVariables>(GET_LOCATIONS);
    const { openModal } = useManageModal()
    useEffect(() => {
        const _location = data?.locations.find((location: any) => location.id === defaultLocation?.id)
        const customFields: CustomField[] = _location?.customFields.map((field: any): CustomField => ({
            id: field.id,
            name: field.name ?? "",
            type: field.type ?? ""
        })) ?? [];

        setCustomFields(customFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, data]);
    // useEffect(() => {
    //     console.log("PEOPLE_PAGE_QUERY changed", { loadingPeople, peopleData });
    // }, [loadingPeople, peopleData]);

    useEffect(() => {
        if (!loadingPeople && peopleData?.people?.edges) {
            const _people = peopleData?.people?.edges.filter((edge: any) => !edge.node.expired)?.map((edge: any) => ({
                label: `${edge.node.firstName} ${edge.node.lastName}`,
                id: edge.node.id
            }))
            setLocationPeople(_people)
        }
    }, [loadingPeople, peopleData])
    const handleAddStudentSubmission: FormSubmitCallback<StudentFormSchema> = async (
        values: any
    ): Promise<ReturnType<FormSubmitCallback>> => {
        const customFields = Object.keys(values.customFields ?? {})
            .filter((key) => values?.customFields?.[key] ?? null)
            .map((key) => {
                // Shouldn't be null undefined and nulls are filtered out
                const customField = values.customFields![key];
                if (!customField.id || !customField.value) {
                    return undefined
                }
                return {
                    id: customField.id,
                    value: customField.value,
                } as CustomFieldInput;
            }).filter((field): field is CustomFieldInput => field !== undefined);
        const input = {
            firstName: values.firstName,
            middleName: values.middleName,
            lastName: values.lastName,
            dob: values.dob ? new Date(values.dob).toISOString() : null,
            postalCode: values.postalCode,
            parentEmail: values.parentEmail,
            parentPhone: values.parentPhone,
            email: values.email,
            personIds: values.personIds,
            customFields: customFields ?? [],
        };
        try {
            const response = await addStudent({
                variables: { input }
            });
            if (response.data?.addStudent.errors) {
                const validationError = response.data.addStudent.errors.find(
                    (e: any) => e.__typename === "ValidationError"
                ) as ApiValidationError;

                const fieldErrors = validationError
                    ? mapApiFieldValidationErrors(validationError)
                    : null;
                if (fieldErrors == null) {
                    return;
                } else {
                    return fieldErrors;
                }
            }
            else {
                console.log("why are we firing")
                onSubmit();
                toggleDrawer();
            }
        } catch (e) {
            if ((e as Error)?.name === "ApolloError") {
                const apolloError = e as ApolloError;

                const isDuplicate = apolloError.graphQLErrors.some(
                    error =>
                        typeof error.extensions?.exception === "object" &&
                        error.extensions.exception !== null &&
                        "message" in error.extensions.exception &&
                        typeof error.extensions.exception.message === "string" &&
                        error.extensions.exception.message.includes("Duplicate record")
                );

                if (isDuplicate) {
                    openModal({
                        type: "DUPLICATE_STUDENT",
                        cancelButtonLabel: "Close"
                    });
                    return;
                }
            }
            openModal({ type: "GENERAL_ERROR" })
        }
    };
    return (
        <StudentForm
            parentEmailNeeded={parentEmailNeeded}
            onSubmit={handleAddStudentSubmission}
            roles={roles}
            people={locationPeople}
            customFields={customFields}
            initialValues={null}
            onCancel={() => toggleDrawer()}
        />
    );
}
